import { useEffect, useRef } from "react";

export const useOnDragStart = (fn: (el: HTMLElement) => void) => {
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  });

  const mouseMoveListenerRef = useRef<((e: MouseEvent | TouchEvent) => void) | null>(null);
  const mouseUpListenerRef = useRef<((e: MouseEvent | TouchEvent) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cb = (e: React.MouseEvent | React.TouchEvent) => {
    const x =
      e.type === "mousedown"
        ? (e as React.MouseEvent).clientX
        : (e as React.TouchEvent).touches[0].clientX;
    const y =
      e.type === "mousedown"
        ? (e as React.MouseEvent).clientY
        : (e as React.TouchEvent).touches[0].clientY;
    const el = e.currentTarget as HTMLElement;
    if (e.type === "mousedown" && (e as React.MouseEvent).button !== 0) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();

    dragStartRef.current = { x, y };
    mouseMoveListenerRef.current = (e: MouseEvent | TouchEvent) => {
      const x =
        e.type === "mousemove" ? (e as MouseEvent).clientX : (e as TouchEvent).touches[0].clientX;
      const y =
        e.type === "mousemove" ? (e as MouseEvent).clientY : (e as TouchEvent).touches[0].clientY;
      if (!dragStartRef.current) {
        return;
      }
      const dx = x - dragStartRef.current.x;
      const dy = y - dragStartRef.current.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        dragStartRef.current = null;
        if (mouseMoveListenerRef.current && mouseUpListenerRef.current) {
          el.removeEventListener("mousemove", mouseMoveListenerRef.current);
          el.removeEventListener("touchmove", mouseMoveListenerRef.current);
          el.removeEventListener("mouseup", mouseUpListenerRef.current);
          mouseUpListenerRef.current = null;
          mouseMoveListenerRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        fnRef.current(el);
      }
    };
    el.addEventListener("mousemove", mouseMoveListenerRef.current);

    mouseUpListenerRef.current = () => {
      if (dragStartRef.current && mouseMoveListenerRef.current && mouseUpListenerRef.current) {
        dragStartRef.current = null;
        el.removeEventListener("mouseup", mouseUpListenerRef.current);
        el.removeEventListener("mousemove", mouseMoveListenerRef.current);
        el.removeEventListener("touchmove", mouseMoveListenerRef.current);
        mouseUpListenerRef.current = null;
        mouseMoveListenerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    el.addEventListener("mouseup", mouseUpListenerRef.current);

    timeoutRef.current = setTimeout(() => {
      if (dragStartRef.current && mouseMoveListenerRef.current && mouseUpListenerRef.current) {
        dragStartRef.current = null;
        el.removeEventListener("mousemove", mouseMoveListenerRef.current);
        el.removeEventListener("touchmove", mouseMoveListenerRef.current);
        el.removeEventListener("mouseup", mouseUpListenerRef.current);
        mouseUpListenerRef.current = null;
        mouseMoveListenerRef.current = null;
        fnRef.current(el);
      }
    }, 200);
  };

  return {
    onMouseDownCapture: cb,
    onTouchStartCapture: cb,
  };
};
